import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of, shareReplay, throwError } from 'rxjs';
import { EntityTypeEnum, LiquidityStructureEnum } from '@aksia/enums';
import { UTILS } from '@aksia/infrastructure';
import {
  DOMAINS,
  IInvestmentVehicle,
  IManagementCompany,
  InvestmentVehicle,
  ManagementCompany,
  TPeriodicStream,
} from '@aksia/models';
import { EntityService } from '../entity/entity.service';
import {
  TStreamStartDates,
  programAUMStartDate,
  streamTypesFor,
} from './periodic-stream.grid';

//#region Types

export type TPeriodicsEntityRef = {
  entityId: number;
  entityTypeId: EntityTypeEnum;
  companyId?: number;
  streamTypes?: Array<TPeriodicStream>;
};

export type TPeriodicsProfile = {
  key: string;
  startDates: TStreamStartDates;
  missingStartDate: boolean;
  investmentProgramId?: number;
  isOpenEnd?: boolean;
  streamTypes: Array<TPeriodicStream>;
};

//#endregion

export const periodicsEntityKey = (
  ref: Pick<TPeriodicsEntityRef, 'entityId' | 'entityTypeId'>,
) => `${ref.entityTypeId}_${ref.entityId}`;

@Injectable({ providedIn: 'root' })
export class PeriodicsProfileService {
  readonly #entitySvc = inject(EntityService);
  readonly #cache = new Map<string, Observable<TPeriodicsProfile>>();

  profile(ref: TPeriodicsEntityRef): Observable<TPeriodicsProfile> {
    const cacheKey =
      ref.entityTypeId === EntityTypeEnum.Program
        ? `${periodicsEntityKey(ref)}_${ref.companyId ?? ''}`
        : periodicsEntityKey(ref);
    let profile$ = this.#cache.get(cacheKey);
    if (!profile$) {
      profile$ = this.#load(ref).pipe(
        catchError((error) => {
          this.#cache.delete(cacheKey);
          return throwError(() => error);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
      this.#cache.set(cacheKey, profile$);
    }
    return profile$;
  }

  #load(ref: TPeriodicsEntityRef): Observable<TPeriodicsProfile> {
    const key = periodicsEntityKey(ref);
    switch (ref.entityTypeId) {
      case EntityTypeEnum.Fund:
        return this.#entitySvc
          .getEntityById<IInvestmentVehicle>(
            ref.entityId,
            DOMAINS.INVESTMENT_VEHICLE.URI.GET,
          )
          .pipe(
            map((fund) => {
              const isOpenEnd =
                fund?.liquidityStructure === LiquidityStructureEnum.OpenEnd;
              const stream = UTILS.DATE.toDate(
                InvestmentVehicle.getStartDate(fund as InvestmentVehicle),
              );
              return {
                key,
                startDates: { stream },
                missingStartDate: !stream,
                investmentProgramId: fund?.investmentProgramId,
                isOpenEnd,
                streamTypes: streamTypesFor(ref.entityTypeId, isOpenEnd),
              };
            }),
          );
      case EntityTypeEnum.ManagementCompany:
        return this.#entitySvc
          .getEntityById<IManagementCompany>(
            ref.entityId,
            DOMAINS.COMPANY.URI.GET,
          )
          .pipe(
            map((company) => {
              const companyAUM = ManagementCompany.getStartDate(
                company as ManagementCompany,
              );
              return {
                key,
                startDates: { companyAUM },
                missingStartDate: !companyAUM,
                streamTypes: streamTypesFor(ref.entityTypeId),
              };
            }),
          );
      case EntityTypeEnum.Program: {
        const profile = (programAUM?: Date): TPeriodicsProfile => ({
          key,
          startDates: { programAUM },
          missingStartDate: false,
          streamTypes: streamTypesFor(ref.entityTypeId),
        });
        if (!ref.companyId) return of(profile());
        return this.#entitySvc
          .getEntityById<IManagementCompany>(
            ref.companyId,
            DOMAINS.COMPANY.URI.GET,
          )
          .pipe(
            map((company) =>
              profile(
                programAUMStartDate(
                  company?.investmentPrograms?.find(
                    (program) => program.investmentProgramId === ref.entityId,
                  )?.funds,
                ),
              ),
            ),
          );
      }
      default:
        return of({
          key,
          startDates: {},
          missingStartDate: false,
          streamTypes: [],
        });
    }
  }
}
